import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { useMatchMediaTierProp } from 'containers/MatchMediaProvider';

import { tieredProp } from 'utilsTS/prop-types';
import type { OptionalTiersObject } from 'utilsTS/react';

import styles from './Flex.module.scss';

export enum FLEX_DISPLAY {
    FLEX = 'flex',
    INLINE_FLEX = 'inline-flex',
}

export enum FLEX_DIRECTION {
    ROW = 'row',
    ROW_REVERSE = 'row-reverse',
    COLUMN = 'column',
    COLUMN_REVERSE = 'column-reverse',
}

export enum FLEX_WRAP {
    NOWRAP = 'nowrap',
    WRAP = 'wrap',
    WRAP_REVERSE = 'wrap-reverse',
}

export enum FLEX_JUSTIFY {
    START = 'start',
    END = 'end',
    CENTER = 'center',
    BETWEEN = 'space-between',
    AROUND = 'space-around',
    EVENLY = 'space-evenly',
}

export enum FLEX_ALIGN {
    STRETCH = 'stretch',
    START = 'start',
    END = 'end',
    CENTER = 'center',
    BASELINE = 'baseline',
}

export enum FLEX_GAP {
    XXS = 'xxs',
    XS = 'xs',
    SM = 'sm',
    MD = 'md',
    LG = 'lg',
    XL = 'xl',
    XXL = 'xxl',
}

export interface FlexProps extends React.ComponentPropsWithoutRef<'div'> {
    /** Defines the HMTL tag */
    tag?: React.ElementType;
    /** Defines (tiered) CSS display property */
    display?: `${FLEX_DISPLAY}` | OptionalTiersObject<`${FLEX_DISPLAY}`>;
    /** Defines (tiered) CSS flex-direction property */
    direction?: `${FLEX_DIRECTION}` | OptionalTiersObject<`${FLEX_DIRECTION}`>;
    /** Defines (tiered) CSS flex-wrap property */
    wrap?: `${FLEX_WRAP}` | OptionalTiersObject<`${FLEX_WRAP}`>;
    /** Defines (tiered) CSS justify-content property  */
    justify?: `${FLEX_JUSTIFY}` | OptionalTiersObject<`${FLEX_JUSTIFY}`>;
    /** Defines (tiered) CSS align-items property */
    align?: `${FLEX_ALIGN}` | OptionalTiersObject<`${FLEX_ALIGN}`>;
    /** Defines (tiered) CSS gap property */
    gap?: `${FLEX_GAP}` | OptionalTiersObject<`${FLEX_GAP}`>;
}

export interface FlexStatics {
    DISPLAY: typeof FLEX_DISPLAY;
    DIRECTION: typeof FLEX_DIRECTION;
    WRAP: typeof FLEX_WRAP;
    JUSTIFY: typeof FLEX_JUSTIFY;
    ALIGN: typeof FLEX_ALIGN;
    GAP: typeof FLEX_GAP;
}

export const Flex: React.FC<FlexProps> & FlexStatics = ({
    tag: Tag = 'div',
    display = 'flex',
    direction = 'row',
    wrap = 'nowrap',
    justify = 'start',
    align = 'stretch',
    gap,
    className,
    children,
    ...rest
}) => {
    const tieredGap = useMatchMediaTierProp(gap);

    return (
        <Tag
            className={cx(
                'g-flex',
                styles.container,
                styles[useMatchMediaTierProp(display)],
                styles[useMatchMediaTierProp(direction)],
                styles[useMatchMediaTierProp(wrap)],
                styles[`justify-${useMatchMediaTierProp(justify)}`],
                styles[`align-${useMatchMediaTierProp(align)}`],
                gap && styles[`gap-${tieredGap}`],
                className,
            )}
            {...rest}
        >
            {children}
        </Tag>
    );
};

Flex.DISPLAY = FLEX_DISPLAY;
Flex.DIRECTION = FLEX_DIRECTION;
Flex.WRAP = FLEX_WRAP;
Flex.JUSTIFY = FLEX_JUSTIFY;
Flex.ALIGN = FLEX_ALIGN;
Flex.GAP = FLEX_GAP;

Flex.displayName = 'Flex';
Flex.propTypes = {
    tag: PropTypes.elementType as React.Validator<React.ElementType>,
    display: tieredProp(PropTypes.oneOf(Object.values(FLEX_DISPLAY))),
    direction: tieredProp(PropTypes.oneOf(Object.values(FLEX_DIRECTION))),
    wrap: tieredProp(PropTypes.oneOf(Object.values(FLEX_WRAP))),
    justify: tieredProp(PropTypes.oneOf(Object.values(FLEX_JUSTIFY))),
    align: tieredProp(PropTypes.oneOf(Object.values(FLEX_ALIGN))),
    gap: tieredProp(PropTypes.oneOf(Object.values(FLEX_GAP))),
    className: PropTypes.string,
    style: PropTypes.shape({}),
    children: PropTypes.node,
};
